<template>
  <v-row no-gutters class="justify-center">
    <v-card width="900px">
      <v-card-title> Complete your registration </v-card-title>
      <v-stepper v-model="registrationStep" vertical elevation="0">
        <v-stepper-step
          :complete="registrationStep > 1"
          step="1"
          color="companyPrimary"
        >
          General Informations
        </v-stepper-step>
        <v-stepper-content step="1">
          <v-form ref="formGeneralData" v-model="formValidationGeneralInfo">
            <v-row no-gutters class="my-2">
              <v-col cols="4">
                <v-text-field
                  v-model="user.phoneNumber"
                  class="mr-3"
                  label="Phone Number*"
                  color="companyPrimary"
                  outlined
                  dense
                  type="tel"
                  counter="28"
                  maxlength="28"
                  hide-spin-buttons
                  placeholder="+XX-1234567890"
                  :rules="[rules.phone, rules.required]"
                  autocomplete="null"
                />
              </v-col>
              <v-col cols="4">
                <BaseDatePicker
                  v-model="user.dateOfBirth"
                  label="Birth Date*"
                  text-field-class="mr-3"
                  :rules="[rules.required]"
                  :max="getMaxDobDate"
                  active-picker="YEAR"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="4">
                <v-select
                  v-model="user.howKnowIason"
                  :label="`How Knows ${getLegalEntity.label}*`"
                  :items="getCodeLabelSelectItems('howKnowIason')"
                  color="companyPrimary"
                  outlined
                  dense
                  :rules="[rules.required]"
                  @change="clearHowKnowsOther"
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="user.anotherReason"
                  :label="`How Knows ${getLegalEntity.label} - Details`"
                  color="companyPrimary"
                  :disabled="user.howKnowIason != 0"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12">
                <v-autocomplete
                  v-model="nationalities"
                  label="Citizenship*"
                  :items="getCodeLabelSelectItems('country')"
                  color="companyPrimary"
                  outlined
                  dense
                  deletable-chips
                  multiple
                  small-chips
                  :rules="[rules.required]"
                />
              </v-col>
              <v-col cols="12">
                <v-checkbox
                  v-model="notItalyDomicile"
                  dense
                  color="companyPrimary"
                  label="I don't live in Italy"
                  class="ml-3"
                  @change="onNotItalyDomicile"
                />
              </v-col>
              <BaseDomicileInfoEdit
                v-if="!notItalyDomicile"
                outlined
                @domicile-region="onDomicileRegion"
                @domicile-province="onDomicileProvince"
                @domicile-municipality="onDomicileMunicipality"
              />
              <v-col v-if="!notItalyDomicile" cols="12">
                <v-text-field
                  v-model="address"
                  label="Address"
                  placeholder="Street, number"
                  color="companyPrimary"
                  hide-spin-buttons
                  outlined
                  dense
                  :rules="[rules.address]"
                  autocomplete="null"
                />
              </v-col>
              <v-col cols="6">
                <BaseDatePicker
                  v-model="user.availabilityDate"
                  label="Availability Date*"
                  text-field-class="mr-3"
                  :min="getTodayDate"
                  outlined
                  dense
                  :rules="[rules.required]"
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="user.noticePeriod"
                  label="Notice Period"
                  color="companyPrimary"
                  suffix="days"
                  outlined
                  dense
                  :rules="[rules.integerNumber]"
                />
              </v-col>
              <v-col cols="12">
                <v-checkbox
                  v-model="partTimePreference"
                  dense
                  color="companyPrimary"
                  label="I prefer part-time"
                  class="ml-3"
                />
              </v-col>
            </v-row>
          </v-form>
          <v-btn
            :disabled="
              !formValidationGeneralInfo ||
              (!notItalyDomicile &&
                (!domicileRegion || !domicileProvince || !domicileMunicipality))
            "
            color="primary"
            @click="nextStep"
          >
            Next <v-icon right>mdi-chevron-right</v-icon>
          </v-btn>
        </v-stepper-content>
        <v-stepper-step
          :complete="registrationStep > 2"
          step="2"
          color="companyPrimary"
        >
          Resume Upload
        </v-stepper-step>
        <v-stepper-content step="2">
          <v-form ref="formUploadResume" v-model="formValidationUploadFile">
            <v-row no-gutters class="my-2">
              <v-col cols="12">
                <v-file-input
                  v-model="resumeFile"
                  accept=".pdf"
                  label="Upload your Resume*"
                  outlined
                  dense
                  prepend-icon=""
                  append-icon="mdi-paperclip"
                  color="companyPrimary"
                  :rules="[rules.required, rules.fileSize]"
                />
              </v-col>
            </v-row>
          </v-form>
          <v-btn plain color="secondary" class="mr-2" @click="previousStep">
            Back
          </v-btn>
          <v-btn
            :disabled="!formValidationUploadFile"
            color="primary"
            @click="nextStep"
          >
            Next <v-icon right>mdi-chevron-right</v-icon>
          </v-btn>
        </v-stepper-content>
        <v-stepper-step
          :complete="registrationStep > 3"
          step="3"
          color="companyPrimary"
        >
          <RegistrationAddEducation
            ref="registrationAddEducation"
            v-model="dialogs"
            :qualifications="qualifications"
            :registration-step="registrationStep"
            :institutes-list="institutesList"
            @open-dialog-qualifications="openDialog('qualifications')"
            @add-item-qualifications="addItem('qualifications')"
            @close-dialog-qualifications="closeDialog('qualifications')"
          />
        </v-stepper-step>
        <v-stepper-content step="3">
          <v-row v-if="qualifications.length > 0" no-gutters class="mb-5">
            <div v-for="(skill, index) in qualifications" :key="index">
              <v-menu
                bottom
                right
                transition="scale-transition"
                origin="top left"
              >
                <template #activator="{ on }">
                  <v-chip
                    label
                    close
                    class="mr-2"
                    v-on="on"
                    @click:close="
                      removeSkillByStringify('qualifications', skill)
                    "
                  >
                    {{
                      `${getLabel("qualification", skill.level)}: ${
                        skill.faculty
                      }`
                    }}
                  </v-chip>
                </template>
                <v-card>
                  <v-card-title>
                    {{ `${skill.faculty} - ${skill.course}` }}</v-card-title
                  >
                  <v-card-subtitle>
                    {{
                      `${getInstituteLabel(skill)} ${formatDateToIso(
                        skill.date
                      )} - ${getMarkString(skill)}`
                    }}
                  </v-card-subtitle>
                </v-card>
              </v-menu>
            </div>
          </v-row>
          <v-checkbox
            v-model="noEducation"
            class="ml-3"
            label="I don't have any education"
            :disabled="qualifications.length > 0"
          />
          <v-btn plain color="secondary" class="mr-2" @click="previousStep">
            Back
          </v-btn>
          <v-btn
            color="primary"
            :disabled="!noEducation && qualifications.length == 0"
            @click="nextStep"
          >
            Next<v-icon right>mdi-chevron-right</v-icon>
          </v-btn>
        </v-stepper-content>
        <v-stepper-step
          :complete="registrationStep > 4"
          step="4"
          color="companyPrimary"
        >
          <RegistrationAddLanguageSkills
            ref="registrationAddLanguageSkills"
            v-model="dialogs"
            :language-skills="languageSkills"
            :registration-step="registrationStep"
            @open-dialog-language-skills="openDialog('languageSkills')"
            @close-dialog-language-skills="closeDialog('languageSkills')"
            @add-item-language-skills="addItem('languageSkills')"
          />
        </v-stepper-step>
        <v-stepper-content step="4">
          <v-row v-if="languageSkills.length > 0" no-gutters class="mb-5">
            <div v-for="(skill, index) in languageSkills" :key="index">
              <v-menu
                bottom
                right
                transition="scale-transition"
                origin="top left"
              >
                <template #activator="{ on }">
                  <v-chip
                    label
                    close
                    class="mr-2"
                    v-on="on"
                    @click:close="
                      removeSkillByStringify('languageSkills', skill)
                    "
                  >
                    {{ getLabel("langSkill", skill.language) }}
                  </v-chip>
                </template>
                <v-card>
                  <v-card-title>
                    {{ getLabel("langSkill", skill.language) }}</v-card-title
                  >
                  <v-card-subtitle>
                    Level: {{ getLabel("langLevel", skill.level) }}
                    <v-tooltip v-if="skill.motherTongue" right>
                      <template #activator="{ on, attrs }">
                        <v-icon
                          dense
                          color="companyPrimary"
                          class="ml-1"
                          v-bind="attrs"
                          v-on="on"
                        >
                          mdi-star-circle
                        </v-icon>
                      </template>
                      <span>Mother Tongue</span>
                    </v-tooltip>
                  </v-card-subtitle>
                </v-card>
              </v-menu>
            </div>
          </v-row>
          <v-btn plain color="secondary" class="mr-2" @click="previousStep">
            Back
          </v-btn>
          <v-btn
            color="primary"
            :disabled="languageSkills.length == 0"
            @click="nextStep"
          >
            Next <v-icon right>mdi-chevron-right</v-icon></v-btn
          >
        </v-stepper-content>
        <v-stepper-step
          :complete="registrationStep > 5"
          step="5"
          color="companyPrimary"
        >
          <RegistrationAddItSkill
            ref="registrationAddItSkill"
            v-model="dialogs"
            :it-skills="itSkills"
            :registration-step="registrationStep"
            @open-dialog-itSkills="openDialog('itSkills')"
            @close-dialog-itSkills="closeDialog('itSkills')"
            @add-item-itSkills="addItem('itSkills')"
          />
        </v-stepper-step>
        <v-stepper-content step="5">
          <v-row v-if="itSkills.length > 0" no-gutters class="mb-5">
            <v-chip
              v-for="(skill, index) in itSkills"
              :key="index"
              label
              close
              class="mr-2"
              @click:close="removeSkillByStringify('itSkills', skill)"
            >
              {{
                `${getLabel("itSkill", skill.topic)}: ${getLabel(
                  "itLevel",
                  skill.level
                )}`
              }}
            </v-chip>
          </v-row>
          <v-checkbox
            v-model="noItSkill"
            class="ml-3"
            label="I don't have any IT skill"
            :disabled="itSkills.length > 0"
          />
          <v-btn plain color="secondary" class="mr-2" @click="previousStep">
            Back
          </v-btn>
          <v-btn
            color="primary"
            :disabled="!noItSkill && itSkills.length == 0"
            @click="nextStep"
          >
            Next <v-icon right>mdi-chevron-right</v-icon></v-btn
          >
        </v-stepper-content>

        <v-stepper-step
          :complete="registrationStep > 6"
          step="6"
          color="companyPrimary"
        >
          <RegistrationAddWorkExperience
            ref="registrationAddWorkExperience"
            v-model="dialogs"
            :work-experiences="workExperiences"
            :registration-step="registrationStep"
            @open-dialog-work-experience="openDialog('workExperiences')"
            @add-item-work-experience="addItem('workExperiences')"
            @close-dialog-work-experience="closeDialog('workExperiences')"
          />
        </v-stepper-step>
        <v-stepper-content step="6">
          <v-row v-if="workExperiences.length > 0" no-gutters class="mb-5">
            <div v-for="(skill, index) in workExperiences" :key="index">
              <v-menu
                bottom
                right
                transition="scale-transition"
                origin="top left"
              >
                <template #activator="{ on }">
                  <v-chip
                    label
                    close
                    class="mr-2"
                    v-on="on"
                    @click:close="
                      removeSkillByStringify('workExperiences', skill)
                    "
                  >
                    {{
                      `${skill.company}: ${formatDateToIso(
                        skill.startDate
                      )} > ${formatDateToIso(skill.endDate)}`
                    }}
                  </v-chip>
                </template>
                <v-card>
                  <v-card-title>
                    {{
                      `${skill.company}: ${getLabel("workRole", skill.role)}`
                    }}</v-card-title
                  >
                  <v-card-subtitle>
                    {{
                      `${getLabel("workArea", skill.area)} - ${getLabel(
                        "workContract",
                        skill.contractType
                      )} from ${formatDateToIso(
                        skill.startDate
                      )} to ${formatDateToIso(skill.endDate)}`
                    }}
                  </v-card-subtitle>
                  <v-card-text>
                    {{ skill.roleAnother }}
                  </v-card-text>
                </v-card>
              </v-menu>
            </div>
          </v-row>
          <v-checkbox
            v-model="noWorkExperience"
            class="ml-3"
            label="I don't have any work experience"
            :disabled="workExperiences.length > 0"
          />
          <v-btn plain color="secondary" class="mr-2" @click="previousStep">
            Back
          </v-btn>
          <v-btn
            color="primary"
            :disabled="!noWorkExperience && workExperiences.length == 0"
            @click="nextStep"
          >
            Next <v-icon right>mdi-chevron-right</v-icon></v-btn
          >
        </v-stepper-content>
        <v-stepper-step
          :complete="registrationStep > 7"
          step="7"
          color="companyPrimary"
        >
          <v-badge
            dot
            color="pink"
            :value="currentCompensation.length && registrationStep != 7"
          >
            Current Compensation
          </v-badge>
          <v-btn
            v-if="registrationStep == 7"
            text
            small
            :color="currentCompensation.length == 0 ? 'success' : 'error'"
            class="ml-3"
            @click="
              currentCompensation.length == 0
                ? openDialog('currentCompensation')
                : currentCompensation.pop()
            "
            ><v-icon small left>{{
              currentCompensation.length == 0
                ? "mdi-plus"
                : "mdi-delete-outline"
            }}</v-icon
            >{{ currentCompensation.length == 0 ? "Add" : "Delete" }}</v-btn
          >
        </v-stepper-step>
        <v-stepper-content step="7"
          ><v-row v-if="currentCompensation.length > 0">
            <v-card-text class="mb-3">
              Thanks, you filled the current compensation form.
            </v-card-text>
          </v-row>
          <v-btn plain color="secondary" class="mr-2" @click="previousStep">
            Back
          </v-btn>
          <v-btn color="primary" @click="nextStep">
            Next <v-icon right>mdi-chevron-right</v-icon></v-btn
          >
        </v-stepper-content>
        <v-stepper-step
          :complete="registrationStep > 8"
          step="8"
          color="companyPrimary"
        >
          <v-badge
            dot
            color="pink"
            :value="requestedCompensation.length && registrationStep != 8"
          >
            Requested Compensation
          </v-badge>
          <v-btn
            v-if="registrationStep == 8"
            text
            small
            :color="requestedCompensation.length == 0 ? 'success' : 'error'"
            class="ml-3"
            @click="
              requestedCompensation.length == 0
                ? openDialog('requestedCompensation')
                : requestedCompensation.pop()
            "
            ><v-icon small left>{{
              requestedCompensation.length == 0
                ? "mdi-plus"
                : "mdi-delete-outline"
            }}</v-icon
            >{{ requestedCompensation.length == 0 ? "Add" : "Delete" }}</v-btn
          >
        </v-stepper-step>
        <v-stepper-content step="8"
          ><v-row v-if="requestedCompensation.length > 0">
            <v-card-text class="mb-3">
              Thanks, you filled the requested compensation form.
            </v-card-text>
          </v-row>
          <v-btn plain color="secondary" class="mr-2" @click="previousStep">
            Back
          </v-btn>
          <v-btn color="primary" :loading="isSaveOngoing" @click="saveUser">
            Save <v-icon right>mdi-content-save-outline</v-icon></v-btn
          >
        </v-stepper-content>
      </v-stepper>
    </v-card>
    <v-dialog
      v-model="dialogs.currentCompensation.show"
      persistent
      max-width="700px"
    >
      <v-card>
        <v-card-title>
          <span class="text-h5">Add Current Compensation</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-form
              ref="currentCompensationForm"
              v-model="dialogs.currentCompensation.formValidation"
            >
              <v-row>
                <v-col cols="12">
                  <v-autocomplete
                    v-model="
                      dialogs.currentCompensation.data.contractualFrameworkId
                    "
                    label="Contractual Framework*"
                    :items="contractualFrameworkList"
                    color="companyPrimary"
                    :rules="[rules.required]"
                  />
                </v-col>
                <v-col cols="6">
                  <v-select
                    v-model="dialogs.currentCompensation.data.contractTypeId"
                    label="Contract Type*"
                    :items="getCodeLabelSelectItems('workContract')"
                    color="companyPrimary"
                    :rules="[rules.required]"
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="dialogs.currentCompensation.data.compensation"
                    label="Compensation*"
                    color="companyPrimary"
                    suffix="€/year gross"
                    :rules="[rules.required, rules.integerNumber]"
                  />
                </v-col>
                <v-col cols="4">
                  <v-checkbox
                    v-model="dialogs.currentCompensation.data.mealVoucher"
                    label="Meal Voucher"
                    color="companyPrimary"
                  ></v-checkbox>
                </v-col>
                <v-col cols="4">
                  <v-checkbox
                    v-model="dialogs.currentCompensation.data.businessCar"
                    label="Business Car"
                    color="companyPrimary"
                  ></v-checkbox>
                </v-col>
                <v-col cols="4">
                  <v-checkbox
                    v-model="dialogs.currentCompensation.data.businessPhone"
                    label="Business Phone"
                    color="companyPrimary"
                  ></v-checkbox>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    v-model="dialogs.currentCompensation.data.otherBenefits"
                    label="Other Benefits"
                    rows="3"
                    no-resize
                    color="companyPrimary"
                  ></v-textarea>
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDialog('currentCompensation')">Cancel</v-btn>
          <v-btn
            :disabled="!dialogs.currentCompensation.formValidation"
            color="info"
            text
            @click="addItem('currentCompensation')"
            >Add</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="dialogs.requestedCompensation.show"
      persistent
      max-width="700px"
    >
      <v-card>
        <v-card-title>
          <span class="text-h5">Add Requested Compensation</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-form
              ref="requestedCompensationForm"
              v-model="dialogs.requestedCompensation.formValidation"
            >
              <v-row>
                <v-col cols="6">
                  <v-select
                    v-model="dialogs.requestedCompensation.data.contractTypeId"
                    label="Contract Type*"
                    :items="getCodeLabelSelectItems('workContract')"
                    color="companyPrimary"
                    :rules="[rules.required]"
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="dialogs.requestedCompensation.data.compensation"
                    label="Compensation*"
                    color="companyPrimary"
                    suffix="€/year gross"
                    :rules="[rules.required, rules.integerNumber]"
                  />
                </v-col>
                <v-col cols="4">
                  <v-checkbox
                    v-model="dialogs.requestedCompensation.data.mealVoucher"
                    label="Meal Voucher"
                    color="companyPrimary"
                  ></v-checkbox>
                </v-col>
                <v-col cols="4">
                  <v-checkbox
                    v-model="dialogs.requestedCompensation.data.businessCar"
                    label="Business Car"
                    color="companyPrimary"
                  ></v-checkbox>
                </v-col>
                <v-col cols="4">
                  <v-checkbox
                    v-model="dialogs.requestedCompensation.data.businessPhone"
                    label="Business Phone"
                    color="companyPrimary"
                  ></v-checkbox>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    v-model="dialogs.requestedCompensation.data.otherBenefits"
                    label="Other Benefits"
                    rows="3"
                    no-resize
                    color="companyPrimary"
                  ></v-textarea>
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDialog('requestedCompensation')"
            >Cancel</v-btn
          >
          <v-btn
            :disabled="!dialogs.requestedCompensation.formValidation"
            color="info"
            text
            @click="addItem('requestedCompensation')"
            >Add</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { validationRules } from "@/utils/validation-rules";
import { getCodeLabelSelectItems, getLabel } from "@/utils/enums";
import {
  fromIsoDateFormatter,
  getMaxDobDate,
  getTodayDate,
  toIsoDateFormatter,
} from "@/utils/date";
import { file2Base64 } from "@/utils/file";
import { AuthService, RegistrationService } from "@/services";
import BaseDomicileInfoEdit from "@/components/base/BaseDomicileInfoEdit.vue";
import RegistrationAddEducation from "@/components/registration/RegistrationAddEducation.vue";
import RegistrationAddLanguageSkills from "@/components/registration/RegistrationAddLanguageSkills.vue";
import RegistrationAddItSkill from "@/components/registration/RegistrationAddItSkill.vue";
import RegistrationAddWorkExperience from "@/components/registration/RegistrationAddWorkExperience.vue";
import BaseDatePicker from "@/components/base/BaseDatePicker.vue";

export default {
  name: "RegistrationFinalizeView",
  components: {
    BaseDomicileInfoEdit,
    RegistrationAddEducation,
    RegistrationAddLanguageSkills,
    RegistrationAddItSkill,
    RegistrationAddWorkExperience,
    BaseDatePicker,
  },
  data() {
    return {
      registrationStep: 1,
      institutesList: null,
      contractualFrameworkList: null,
      resumeFile: null,

      formValidationGeneralInfo: true,
      formValidationUploadFile: true,
      noEducation: false,
      noItSkill: false,
      noWorkExperience: false,
      notItalyDomicile: false,
      partTimePreference: false,

      isSaveOngoing: false,

      confirmMail: null,

      user: {
        phoneNumber: "",
        dateOfBirth: "",
        availabilityDate: null,
        noticePeriod: null,
        howKnowIason: null,
        anotherReason: null,
      },

      nationalities: [],
      domicileRegion: null,
      domicileProvince: null,
      domicileMunicipality: null,
      address: null,
      itSkills: [],
      languageSkills: [],
      qualifications: [],
      workExperiences: [],
      currentCompensation: [],
      requestedCompensation: [],

      dialogs: {
        itSkills: {
          show: false,
          data: {
            topic: null,
            level: null,
          },
        },
        languageSkills: {
          show: false,
          data: {
            language: null,
            level: null,
            certification: null,
            motherTongue: null,
          },
        },
        qualifications: {
          show: false,
          data: {
            level: null,
            instituteId: null,
            faculty: null,
            course: null,
            date: null,
            mark: null,
            markScale: null,
            cumLaude: null,
          },
        },
        workExperiences: {
          show: false,
          data: {
            company: null,
            area: null,
            role: null,
            roleAnother: null,
            contractType: null,
            startDate: null,
            endDate: null,
          },
        },
        currentCompensation: {
          show: false,
          formValidation: true,
          data: {
            contractualFrameworkId: null,
            contractTypeId: null,
            compensation: null,
            mealVoucher: null,
            businessCar: null,
            businessPhone: null,
            otherBenefits: null,
          },
        },
        requestedCompensation: {
          show: false,
          formValidation: true,
          data: {
            contractTypeId: null,
            compensation: null,
            mealVoucher: null,
            businessCar: null,
            businessPhone: null,
            otherBenefits: null,
          },
        },
      },
      rules: { ...validationRules },

      getCodeLabelSelectItems,
      getLabel,
    };
  },
  computed: {
    ...mapGetters("user", {
      getUserId: "getUserId",
      isRegistrationCompleted: "isRegistrationCompleted",
    }),
    ...mapGetters("domain", ["getLegalEntity"]),

    getMaxDobDate() {
      return getMaxDobDate();
    },
    getTodayDate() {
      return getTodayDate();
    },
  },
  async beforeCreate() {
    if (this.$store.getters["user/isAuthenticated"]) {
      await AuthService.checkAuthorization();
    }

    if (this.$store.getters["user/isRegistrationCompleted"]) {
      this.$router.push({ name: "dashboard" });
    }
  },
  created() {
    this.loadInitialData();
  },
  methods: {
    ...mapActions({
      setSnackBar: "setSnackBar",
      fetchInstituteList: "institutes/fetchInstituteList",
      fetchContractualFrameworkList:
        "contractualFrameworks/fetchContractualFrameworkList",
      setRegistrationCompletedTs: "user/setRegistrationCompletedTs",
    }),
    async loadInitialData() {
      this.institutesList = await this.fetchInstituteList();
      this.contractualFrameworkList =
        await this.fetchContractualFrameworkList();
    },
    nextStep() {
      this.registrationStep++;
    },
    previousStep() {
      this.registrationStep--;
    },
    onDomicileRegion(event) {
      this.domicileRegion = event;
    },
    onDomicileProvince(event) {
      this.domicileProvince = event;
    },
    onDomicileMunicipality(event) {
      this.domicileMunicipality = event;
    },
    openDialog(property) {
      this.dialogs[property].show = true;
      if (property === "languageSkills")
        this.dialogs[property].data.motherTongue = false;
      if (property === "qualifications")
        this.dialogs[property].data.cumLaude = false;
    },
    closeDialog(property) {
      this.dialogs[property].show = false;
      Object.keys(this.dialogs[property].data).forEach(
        (k) => (this.dialogs[property].data[k] = null)
      );
      let formRef = this.getFormRefByProperty(property);
      this.resetValidation(formRef);
    },
    addItem(property) {
      let item = Object.assign({}, this.dialogs[property].data);
      this[property].push(item);
      this.closeDialog(property);
    },
    clearHowKnowsOther() {
      if (this.user.howKnowIason !== 0) {
        this.user.anotherReason = null;
      }
    },
    removeSkillByStringify(skillType, obj) {
      this[skillType] = this[skillType].filter(
        (el) => JSON.stringify(obj) !== JSON.stringify(el)
      );
    },
    getMarkString(skill) {
      let additionalString = "";
      if (skill.cumLaude) {
        additionalString = "Cum Laude";
      }
      return `${skill.mark}/${skill.markScale} ${additionalString}`;
    },
    getInstituteLabel(skill) {
      if (skill.instituteId == null) return "";
      return (
        this.institutesList.find((el) => {
          return el.value == skill.instituteId;
        }).text + " -"
      );
    },
    formatDateToIso(date) {
      return toIsoDateFormatter(date);
    },
    formatDateFromIso(date) {
      return fromIsoDateFormatter(date);
    },
    getFormRefByProperty(property) {
      switch (property) {
        case "itSkills":
          return "formItSkills";
        case "languageSkills":
          return "formLanguageSkills";
        case "qualifications":
          return "formQualifications";
        case "workExperiences":
          return "formWorkExperiences";
        default:
          return `${property}Form`;
      }
    },
    resetValidation(formRef) {
      if (!formRef) return;
      let localForm;
      switch (formRef) {
        case "formQualifications":
          localForm =
            this.$refs["registrationAddEducation"].$refs[
              "registrationAddEducationDialog"
            ];
          break;
        case "formLanguageSkills":
          localForm =
            this.$refs["registrationAddLanguageSkills"].$refs[
              "registrationAddLanguageSkillsDialog"
            ];
          break;
        case "formItSkills":
          localForm =
            this.$refs["registrationAddItSkill"].$refs[
              "registrationAddItSkillDialog"
            ];
          break;
        case "formWorkExperiences":
          localForm =
            this.$refs["registrationAddWorkExperience"].$refs[
              "registrationAddWorkExperienceDialog"
            ];
          break;
        default:
          localForm = this;
      }
      localForm.$refs[formRef].resetValidation();
    },
    async saveUser() {
      try {
        this.isSaveOngoing = true;

        let registrationDto = structuredClone(this.user);

        registrationDto.dateOfBirth = this.formatDateFromIso(
          registrationDto.dateOfBirth
        );
        registrationDto.availabilityDate = this.formatDateFromIso(
          registrationDto.availabilityDate
        );
        registrationDto.noticePeriod =
          registrationDto.noticePeriod === ""
            ? null
            : registrationDto.noticePeriod;

        registrationDto.resume = await this.getResumeDto();

        registrationDto.domicileRegionId = this.domicileRegion;
        registrationDto.domicileProvinceId = this.domicileProvince;
        registrationDto.domicileMunicipalityId = this.domicileMunicipality;
        registrationDto.address = this.address;

        registrationDto.partTimePreference = this.partTimePreference;

        if (this.nationalities.length > 0) {
          let data = this.nationalities.map((el) => {
            return {
              userId: this.getUserId,
              nation: el,
            };
          });
          registrationDto.nationalities = data;
        }

        if (this.qualifications.length > 0) {
          let data = structuredClone(this.qualifications);
          data.forEach((el) => {
            el.userId = this.getUserId;
            el.date = this.formatDateFromIso(el.date);
            el.instituteStr = "-"; // legacy backward compatibility
          });
          registrationDto.qualifications = data;
        }

        if (this.languageSkills.length > 0) {
          let data = structuredClone(this.languageSkills);
          data.forEach((el) => {
            el.userId = this.getUserId;
          });
          registrationDto.languageSkills = data;
        }

        if (this.itSkills.length > 0) {
          let data = structuredClone(this.itSkills);
          data.forEach((el) => {
            el.userId = this.getUserId;
          });
          registrationDto.itSkills = data;
        }

        if (this.workExperiences.length > 0) {
          let data = structuredClone(this.workExperiences);
          data.forEach((el) => {
            el.userId = this.getUserId;
            el.startDate = this.formatDateFromIso(el.startDate);
            el.endDate = this.formatDateFromIso(el.endDate);
          });
          registrationDto.workExperiences = data;
        }

        if (this.currentCompensation.length > 0) {
          let data = structuredClone(this.currentCompensation[0]);
          data.userId = this.getUserId;
          registrationDto.currentCompensation = data;
        }

        if (this.requestedCompensation.length > 0) {
          let data = structuredClone(this.requestedCompensation[0]);
          data.userId = this.getUserId;
          registrationDto.requestedCompensation = data;
        }

        await RegistrationService.finalizeRegistration(
          this.getUserId,
          registrationDto
        );
      } finally {
        this.isSaveOngoing = false;
      }
    },
    async getResumeDto() {
      let fileBase64 = null;

      let filename = this.resumeFile.name;
      await file2Base64(this.resumeFile).then((response) => {
        fileBase64 = response.split(",")[1];
      });

      return {
        file: fileBase64,
        filename: filename,
      };
    },
    onNotItalyDomicile() {
      this.domicileRegion = null;
      this.domicileProvince = null;
      this.domicileMunicipality = null;
      this.address = null;
    },
  },
};
</script>
